import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {Object} CartWelcomeDiscountRefs
 * @property {HTMLButtonElement} applyButton - The apply button.
 * @property {HTMLElement} error - The error element.
 */

/**
 * A custom element that applies the first-order welcome discount to the cart.
 *
 * @extends {Component<CartWelcomeDiscountRefs>}
 */
class CartWelcomeDiscount extends Component {
  requiredRefs = ['applyButton', 'error'];

  /**
   * Applies the welcome discount code to the cart.
   */
  apply = async () => {
    const { applyButton, error } = this.refs;
    const sectionId = this.dataset.sectionId;
    const code = this.dataset.code;
    if (!sectionId || !code) return;

    error.classList.add('hidden');
    applyButton.disabled = true;

    try {
      // Keep any codes already applied to the cart.
      const existing = Array.from(document.querySelectorAll('.cart-discount__pill'))
        .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode : null))
        .filter(Boolean);

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            discount: [...new Set([...existing, code])].join(','),
            sections: [sectionId],
          }),
        }),
      });
      const data = await response.json();

      const rejected = data.discount_codes?.find(
        (/** @type {{ code: string; applicable: boolean; }} */ d) => d.code === code && d.applicable === false
      );
      if (!response.ok || rejected) {
        error.classList.remove('hidden');
        applyButton.disabled = false;
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, data.sections[sectionId]);
    } catch (e) {
      error.classList.remove('hidden');
      applyButton.disabled = false;
    }
  };
}

if (!customElements.get('cart-welcome-discount')) {
  customElements.define('cart-welcome-discount', CartWelcomeDiscount);
}
